import { useTranslation } from 'react-i18next';
import type { EmailFlowWidgetItemData } from '@inference.engine/widgets/web';

interface Props {
  data: EmailFlowWidgetItemData | null;
}

const CTA_LABEL_MAX = 24;

/**
 * Pulls the first link label out of the rendered email HTML to echo the email's
 * primary CTA button. Returns null when there's no link or the label is too long
 * to read at thumbnail scale — the email schema carries no structured CTA, so
 * this is best-effort and the chip simply doesn't render when it can't be found.
 */
const firstCtaLabel = (html: string | undefined): string | null => {
  const match = html?.match(/<a\b[^>]*>([\s\S]*?)<\/a>/i);
  if (!match) return null;
  const label = match[1].replace(/<[^>]+>/g, '').trim();
  return label && label.length <= CTA_LABEL_MAX ? label : null;
};

/**
 * Prop-only preview of an email-flow widget: the subject line as the headline,
 * the preheader text, and the primary CTA as a button chip when one can be read
 * from the HTML. Renders props.data only — never touches the widget store or the
 * artifact-save hook. (The hero banner is intentionally omitted: the email data
 * carries no image asset, only the HTML string.)
 */
export const EmailFlowThumbnail = (props: Props) => {
  const { t } = useTranslation('contentTest');
  const subjectLine = props.data?.subjectLine || t('emailFlowWidget.email');
  const preheaderText = props.data?.preheaderText ?? '';
  const ctaLabel = firstCtaLabel(props.data?.htmlContent);

  return (
    <div className="flex h-full w-full flex-col gap-2 overflow-hidden">
      <p className="line-clamp-2 text-sm font-semibold text-[#1A1A1A]">{subjectLine}</p>
      {preheaderText && <p className="line-clamp-3 text-xs text-[#6C6962]">{preheaderText}</p>}
      {ctaLabel && (
        <span className="mt-auto inline-flex w-fit rounded-md bg-[#1A1A1A] px-3 py-1 text-xs font-medium text-white">
          {ctaLabel}
        </span>
      )}
    </div>
  );
};
